import { memo, useCallback, useState } from 'react';
import { removeView, removeViewUser } from '../../services/viewServices';
import Avatar from '../avatar';
import { Dropdown, DropdownItem } from '../dropdown';
import DeleteModal from '../modals/deleteModal';
import Modal from '../modals/modal';
import {
  BlankDiv,
  Table,
  UserName,
  ViewItem,
  AvatarsGroup,
  AvatarWrapper,
  PlusAvatar,
  ViewItemLeftSection
} from './styles';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { RawView } from '../../mobx/interfaces/view';
import { AVATAR_BORDER, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_COLORS } from '../../global/constants';
import ProjectIconAvatar from '../projectIconAvatar';
import { useMobileDevice } from '../../global/useMobile';
import ShareViewModal from '../modals/shareViewModal';
import { ViewModel } from '../../mobx/models/view';
import IconButton from '../button/iconButton';
import { isEmpty } from '../../helpers/common';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routes/urls';

interface ViewItemRowProps {
  view: ViewModel;
  isMobile: boolean;
  onClickDropdownItem: (label: string, viewId: string, view: ViewModel) => void;
  onViewClick: (viewId: string) => void;
}

const ViewItemRow = memo(({ view, isMobile, onClickDropdownItem, onViewClick }: ViewItemRowProps) => {
  const dropdownItems = view.isReadOnly
    ? [{ label: 'Leave', iconName: 'exit-icon' }]
    : [
      { label: 'Edit', iconName: 'edit-icon' },
      { label: 'Share', iconName: 'share-icon' },
      { label: 'Delete', iconName: 'updated-delete-icon' }
    ];

  return (
    <ViewItem role='button' onClick={() => onViewClick(view.id)}>
      <ViewItemLeftSection>
        <ProjectIconAvatar
          type={view.icon.type}
          reference={view.icon.reference || ''}
          backgroundColor={view.icon?.color ? ICON_COLORS[view.icon?.color] : ''}
          projectName={view?.name || ''}
          iconSize={isMobile ? 20 : 16}
          isClickable={false}
          size={isMobile ? 36 : 30}
          fontSize={isMobile ? '20px' : '16px'}
          borderRadius='var(--space-8)'
        />
        <UserName>
          <p>{view.name}</p>
          {!isEmpty(view.description) && <span>{view.description}</span>}
        </UserName>
      </ViewItemLeftSection>
      {!isMobile && (
        <UserName>
          <AvatarsGroup>
            {view.users.slice(0, 4).map((user, i) => (
              <AvatarWrapper key={i}>
                <Avatar
                  name={user.name}
                  size={24}
                  imgSrc={user.profile_photo || ''}
                  borderVariant={AVATAR_BORDER.THIN}
                />
              </AvatarWrapper>
            ))}
            {view.users.length > 4 && <PlusAvatar>+{view.users.length - 4}</PlusAvatar>}
          </AvatarsGroup>
        </UserName>
      )}
      <Dropdown
        activeClassName='active-dropdown'
        modalTitle='Manage view'
        content={
          <IconButton
            iconName='three-dots-horizontal-icon'
            size={ICON_BUTTON_SIZE.SMALL}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            className='more-icon'
          />
        }
        trigger='click'>
        {dropdownItems.map((item) => (
          <DropdownItem
            key={item.label}
            label={item.label}
            iconName={item.iconName}
            iconSize={16}
            tone={item.label === 'Delete' ? 'critical' : 'primary'}
            iconViewBox={item.label === 'Share' ? '0 0 16 16' : '0 0 18 18'}
            onClick={() => onClickDropdownItem(item.label, view.id, view)}
          />
        ))}
      </Dropdown>
    </ViewItem>
  );
});

ViewItemRow.displayName = 'ViewItemRow';

const ViewsTable = () => {
  //state variables
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<ViewModel | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  //Mobx stores variables
  const { viewStore, currentUser } = rootStore;
  const { views, loading } = viewStore;
  //other variables
  const isMobile = useMobileDevice();
  const history = useHistory();

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedViewId) return;
    const result = await removeView(selectedViewId);
    if (result) {
      rootStore.viewStore.setViews(views.filter((view) => view.id !== selectedViewId) as RawView[]);
      setShowDeleteModal(false);
      setSelectedViewId(null);
    }
  }, [selectedViewId, views]);

  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false);
  }, [setShowDeleteModal]);

  // close share modal
  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
  }, [setShowShareModal]);

  const onEditClick = useCallback(
    (viewId: string) => {
      history.push(`${ROUTES.VIEW_ROUTE.replace(':viewId', viewId)}?isEdit=true`);
    },
    [history]
  );

  const onShareClick = useCallback(
    (viewId: string, view: ViewModel, e?: React.MouseEvent<Element, MouseEvent>) => {
      if (e) {
        e.stopPropagation();
      }
      setSelectedViewId(viewId);
      setSelectedView(view);
      setShowShareModal(true);
    },
    []
  );

  const onDeleteClick = useCallback(
    async (viewId: string) => {
      setSelectedViewId(viewId);
      setShowDeleteModal(true);
    },
    [setSelectedViewId, setShowDeleteModal]
  );

  const onClickDropdownItem = useCallback(
    (label: string, viewId: string, view: ViewModel) => {
      if (label === 'Delete') {
        setShowDeleteModal(true);
        onDeleteClick(viewId);
      } else if (label === 'Edit') {
        onEditClick(viewId);
      } else if (label === 'Share') {
        onShareClick(viewId, view);
      } else if (label === 'Leave') {
        removeViewUser({ users: [currentUser?.id] }, viewId);
      }
    },
    [onDeleteClick, onEditClick, onShareClick, currentUser?.id]
  );

  const onViewClick = useCallback(
    (viewId: string) => {
      history.push(ROUTES.VIEW_ROUTE.replace(':viewId', viewId));
    },
    [history]
  );
  return (
    <>
      <Table>
        {/* Table Header */}
        {!isMobile && (
          <ViewItem>
            <UserName>
              <p>Name</p>
            </UserName>
            <UserName>
              <p>Access</p>
            </UserName>
            <BlankDiv />
          </ViewItem>
        )}

        {/* Table Rows */}
        {views.map((view) => (
          <ViewItemRow
            key={view.id}
            view={view}
            isMobile={isMobile}
            onClickDropdownItem={onClickDropdownItem}
            onViewClick={onViewClick}
          />
        ))}
      </Table>
      <Modal open={showDeleteModal} onClose={handleCloseModal} maxWidth={'334px'}>
        <DeleteModal
          modalTitle='Delete View?'
          description='Are you sure you want to delete this view?'
          onClose={handleCloseModal}
          onOk={handleDeleteConfirm}
          buttonText='Delete'
          isPrimaryButton={false}
          loading={loading}
        />
      </Modal>
      <Modal open={showShareModal} onClose={handleCloseShareModal} maxWidth={'360px'}>
        <ShareViewModal onClose={handleCloseShareModal} selectedView={selectedView} />
      </Modal>
    </>
  );
};
export default observer(ViewsTable);
